import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import MobileStickyFooter from './components/MobileStickyFooter';
import CookieConsent from './components/CookieConsent';
import Breadcrumbs from './components/Breadcrumbs';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Contact from './pages/Contact';
import ProtectedRoute from './components/admin/ProtectedRoute';
import { trackPageView, trackMetaEvent, initContactLinkTracking } from './utils/gtag';

// Lazy-loaded page imports
const Radiotherapy = React.lazy(() => import('./pages/Radiotherapy'));
const PersistentPain = React.lazy(() => import('./pages/PersistentPain'));
const Audios = React.lazy(() => import('./pages/Audios'));
const AnxietyStress = React.lazy(() => import('./pages/AnxietyStress'));
const MedicalProcedureAnxiety = React.lazy(() => import('./pages/MedicalProcedureAnxiety'));
const JawTensionHypnotherapy = React.lazy(() => import('./pages/JawTensionHypnotherapy'));
const BladderUrgencyHypnotherapy = React.lazy(() => import('./pages/BladderUrgencyHypnotherapy'));
const IbsHypnotherapy = React.lazy(() => import('./pages/IbsHypnotherapy'));
const MriAnxietyHypnotherapy = React.lazy(() => import('./pages/MriAnxietyHypnotherapy'));
const NeedlePhobiaHypnotherapy = React.lazy(() => import('./pages/NeedlePhobiaHypnotherapy'));
const StressPhysicalSymptoms = React.lazy(() => import('./pages/StressPhysicalSymptoms'));
const MedicalAnxietyHypnotherapy = React.lazy(() => import('./pages/MedicalAnxietyHypnotherapy'));
const WeightMindset = React.lazy(() => import('./pages/WeightMindset'));
const BurnoutRecovery = React.lazy(() => import('./pages/BurnoutRecovery'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPost'));
const Testimonials = React.lazy(() => import('./pages/Testimonials'));
const ThankYou = React.lazy(() => import('./pages/ThankYou'));
const StressSymptomQuiz = React.lazy(() => import('./pages/quiz/StressSymptomQuiz'));
const MedicalAnxietyQuiz = React.lazy(() => import('./pages/quiz/MedicalAnxietyQuiz'));
const BladderUrgencyQuiz = React.lazy(() => import('./pages/quiz/BladderUrgencyQuiz'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const Terms = React.lazy(() => import('./pages/Terms'));
const CookiePolicy = React.lazy(() => import('./pages/CookiePolicy'));
const Book = React.lazy(() => import('./pages/Book'));
const HowChangeHappens = React.lazy(() => import('./pages/HowChangeHappens'));
const HolidayAnxiety = React.lazy(() => import('./pages/HolidayAnxiety'));
const PerformanceAnxiety = React.lazy(() => import('./pages/PerformanceAnxiety'));
const SeasonalAnxiety = React.lazy(() => import('./pages/SeasonalAnxiety'));

// Lazy-loaded location pages
const BelfastHypnotherapy = React.lazy(() => import('./pages/locations/BelfastHypnotherapy'));
const DerryHypnotherapy = React.lazy(() => import('./pages/locations/DerryHypnotherapy'));
const LisburnHypnotherapy = React.lazy(() => import('./pages/locations/LisburnHypnotherapy'));
const NewryHypnotherapy = React.lazy(() => import('./pages/locations/NewryHypnotherapy'));
const BangorHypnotherapy = React.lazy(() => import('./pages/locations/BangorHypnotherapy'));
const NewtownabbeyHypnotherapy = React.lazy(() => import('./pages/locations/NewtownabbeyHypnotherapy'));
const CarrickfergusHypnotherapy = React.lazy(() => import('./pages/locations/CarrickfergusHypnotherapy'));
const LarneHypnotherapy = React.lazy(() => import('./pages/locations/LarneHypnotherapy'));
const AntrimHypnotherapy = React.lazy(() => import('./pages/locations/AntrimHypnotherapy'));
const ArmaghHypnotherapy = React.lazy(() => import('./pages/locations/ArmaghHypnotherapy'));
const CraigavonHypnotherapy = React.lazy(() => import('./pages/locations/CraigavonHypnotherapy'));
const EnniskillenHypnotherapy = React.lazy(() => import('./pages/locations/EnniskillenHypnotherapy'));
const OmaghHypnotherapy = React.lazy(() => import('./pages/locations/OmaghHypnotherapy'));
const StrabaneHypnotherapy = React.lazy(() => import('./pages/locations/StrabaneHypnotherapy'));
const DownpatrickHypnotherapy = React.lazy(() => import('./pages/locations/DownpatrickHypnotherapy'));
const BanbridgeHypnotherapy = React.lazy(() => import('./pages/locations/BanbridgeHypnotherapy'));
const DungannonHypnotherapy = React.lazy(() => import('./pages/locations/DungannonHypnotherapy'));
const CookstownHypnotherapy = React.lazy(() => import('./pages/locations/CookstownHypnotherapy'));
const LurganHypnotherapy = React.lazy(() => import('./pages/locations/LurganHypnotherapy'));
const PortadownHypnotherapy = React.lazy(() => import('./pages/locations/PortadownHypnotherapy'));
const NewtownardsHypnotherapy = React.lazy(() => import('./pages/locations/NewtownardsHypnotherapy'));
const MagherafeltHypnotherapy = React.lazy(() => import('./pages/locations/MagherafeltHypnotherapy'));
const MagheraHypnotherapy = React.lazy(() => import('./pages/locations/MagheraHypnotherapy'));
const LimavadyHypnotherapy = React.lazy(() => import('./pages/locations/LimavadyHypnotherapy'));
const PortrushHypnotherapy = React.lazy(() => import('./pages/locations/PortrushHypnotherapy'));
const PortstewartHypnotherapy = React.lazy(() => import('./pages/locations/PortstewartHypnotherapy'));
const BallycastleHypnotherapy = React.lazy(() => import('./pages/locations/BallycastleHypnotherapy'));
const BushmillsHypnotherapy = React.lazy(() => import('./pages/locations/BushmillsHypnotherapy'));

// Lazy-loaded service + location combo pages
const LurganAnxietyStress = React.lazy(() => import('./pages/services/LurganAnxietyStress'));
const PortadownAnxietyStress = React.lazy(() => import('./pages/services/PortadownAnxietyStress'));
const NewtownardsAnxietyStress = React.lazy(() => import('./pages/services/NewtownardsAnxietyStress'));
const MagherafeltAnxietyStress = React.lazy(() => import('./pages/services/MagherafeltAnxietyStress'));
const LimavadyAnxietyStress = React.lazy(() => import('./pages/services/LimavadyAnxietyStress'));
const DerryAnxietyStress = React.lazy(() => import('./pages/services/DerryAnxietyStress'));
const LisburnAnxietyStress = React.lazy(() => import('./pages/services/LisburnAnxietyStress'));
const NewryAnxietyStress = React.lazy(() => import('./pages/services/NewryAnxietyStress'));
const BangorAnxietyStress = React.lazy(() => import('./pages/services/BangorAnxietyStress'));
const NewtownabbeyAnxietyStress = React.lazy(() => import('./pages/services/NewtownabbeyAnxietyStress'));
const CraigavonAnxietyStress = React.lazy(() => import('./pages/services/CraigavonAnxietyStress'));
const CarrickfergusAnxietyStress = React.lazy(() => import('./pages/services/CarrickfergusAnxietyStress'));
const AntrimAnxietyStress = React.lazy(() => import('./pages/services/AntrimAnxietyStress'));
const OmaghAnxietyStress = React.lazy(() => import('./pages/services/OmaghAnxietyStress'));
const LarneAnxietyStress = React.lazy(() => import('./pages/services/LarneAnxietyStress'));
const DerryChronicPain = React.lazy(() => import('./pages/services/DerryChronicPain'));
const LisburnChronicPain = React.lazy(() => import('./pages/services/LisburnChronicPain'));
const NewryChronicPain = React.lazy(() => import('./pages/services/NewryChronicPain'));
const BangorChronicPain = React.lazy(() => import('./pages/services/BangorChronicPain'));
const NewtownabbeyChronicPain = React.lazy(() => import('./pages/services/NewtownabbeyChronicPain'));
const CraigavonChronicPain = React.lazy(() => import('./pages/services/CraigavonChronicPain'));
const CarrickfergusChronicPain = React.lazy(() => import('./pages/services/CarrickfergusChronicPain'));
const AntrimChronicPain = React.lazy(() => import('./pages/services/AntrimChronicPain'));
const OmaghChronicPain = React.lazy(() => import('./pages/services/OmaghChronicPain'));
const LarneChronicPain = React.lazy(() => import('./pages/services/LarneChronicPain'));
const LurganChronicPain = React.lazy(() => import('./pages/services/LurganChronicPain'));
const PortadownChronicPain = React.lazy(() => import('./pages/services/PortadownChronicPain'));
const NewtownardsChronicPain = React.lazy(() => import('./pages/services/NewtownardsChronicPain'));
const MagherafeltChronicPain = React.lazy(() => import('./pages/services/MagherafeltChronicPain'));
const LimavadyChronicPain = React.lazy(() => import('./pages/services/LimavadyChronicPain'));
const DerryWeightManagement = React.lazy(() => import('./pages/services/DerryWeightManagement'));
const LisburnWeightManagement = React.lazy(() => import('./pages/services/LisburnWeightManagement'));
const NewryWeightManagement = React.lazy(() => import('./pages/services/NewryWeightManagement'));
const BangorWeightManagement = React.lazy(() => import('./pages/services/BangorWeightManagement'));
const NewtownabbeyWeightManagement = React.lazy(() => import('./pages/services/NewtownabbeyWeightManagement'));
const CraigavonWeightManagement = React.lazy(() => import('./pages/services/CraigavonWeightManagement'));
const CarrickfergusWeightManagement = React.lazy(() => import('./pages/services/CarrickfergusWeightManagement'));
const AntrimWeightManagement = React.lazy(() => import('./pages/services/AntrimWeightManagement'));
const OmaghWeightManagement = React.lazy(() => import('./pages/services/OmaghWeightManagement'));
const LarneWeightManagement = React.lazy(() => import('./pages/services/LarneWeightManagement'));
const LurganWeightManagement = React.lazy(() => import('./pages/services/LurganWeightManagement'));
const PortadownWeightManagement = React.lazy(() => import('./pages/services/PortadownWeightManagement'));
const NewtownardsWeightManagement = React.lazy(() => import('./pages/services/NewtownardsWeightManagement'));
const MagherafeltWeightManagement = React.lazy(() => import('./pages/services/MagherafeltWeightManagement'));
const LimavadyWeightManagement = React.lazy(() => import('./pages/services/LimavadyWeightManagement'));
const DerryMedicalProcedureAnxiety = React.lazy(() => import('./pages/services/DerryMedicalProcedureAnxiety'));
const LisburnMedicalProcedureAnxiety = React.lazy(() => import('./pages/services/LisburnMedicalProcedureAnxiety'));
const NewryMedicalProcedureAnxiety = React.lazy(() => import('./pages/services/NewryMedicalProcedureAnxiety'));
const BangorMedicalProcedureAnxiety = React.lazy(() => import('./pages/services/BangorMedicalProcedureAnxiety'));
const NewtownabbeyMedicalProcedureAnxiety = React.lazy(() => import('./pages/services/NewtownabbeyMedicalProcedureAnxiety'));
const DerryQuitSmoking = React.lazy(() => import('./pages/services/DerryQuitSmoking'));
const LisburnQuitSmoking = React.lazy(() => import('./pages/services/LisburnQuitSmoking'));
const NewryQuitSmoking = React.lazy(() => import('./pages/services/NewryQuitSmoking'));
const BangorQuitSmoking = React.lazy(() => import('./pages/services/BangorQuitSmoking'));
const NewtownabbeyQuitSmoking = React.lazy(() => import('./pages/services/NewtownabbeyQuitSmoking'));
const CraigavonQuitSmoking = React.lazy(() => import('./pages/services/CraigavonQuitSmoking'));
const CarrickfergusQuitSmoking = React.lazy(() => import('./pages/services/CarrickfergusQuitSmoking'));
const AntrimQuitSmoking = React.lazy(() => import('./pages/services/AntrimQuitSmoking'));
const OmaghQuitSmoking = React.lazy(() => import('./pages/services/OmaghQuitSmoking'));
const LarneQuitSmoking = React.lazy(() => import('./pages/services/LarneQuitSmoking'));
const CraigavonMedicalAnxiety = React.lazy(() => import('./pages/services/CraigavonMedicalAnxiety'));
const CarrickfergusMedicalAnxiety = React.lazy(() => import('./pages/services/CarrickfergusMedicalAnxiety'));
const AntrimMedicalAnxiety = React.lazy(() => import('./pages/services/AntrimMedicalAnxiety'));
const OmaghMedicalAnxiety = React.lazy(() => import('./pages/services/OmaghMedicalAnxiety'));
const LarneMedicalAnxiety = React.lazy(() => import('./pages/services/LarneMedicalAnxiety'));

// Lazy-loaded admin pages
const AdminLogin = React.lazy(() => import('./pages/admin/Login'));
const AdminDashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const AdminEnquiries = React.lazy(() => import('./pages/admin/Enquiries'));
const AdminPolls = React.lazy(() => import('./pages/admin/Polls'));
const AdminAudioDownloads = React.lazy(() => import('./pages/admin/AudioDownloads'));
const AdminSubscriptions = React.lazy(() => import('./pages/admin/Subscriptions'));
const AdminBlogPosts = React.lazy(() => import('./pages/admin/BlogPosts'));
const AdminBlogAudit = React.lazy(() => import('./pages/admin/BlogAudit'));
const AdminQuizResponses = React.lazy(() => import('./pages/admin/QuizResponses'));
const AdminBookings = React.lazy(() => import('./pages/admin/Bookings'));
const AdminAnalytics = React.lazy(() => import('./pages/admin/Analytics'));
const AdminReviewRequests = React.lazy(() => import('./pages/admin/ReviewRequests'));
const SEOPerformance = React.lazy(() => import('./pages/admin/SEOPerformance'));

// Analytics for client-side navigation.
//
// index.html fires the initial GA4 page_view and Meta PageView on hard load.
// This site is an SPA, so every subsequent navigation used to go completely
// unrecorded: GA4 saw one page_view per session and Meta saw one PageView,
// no matter how many pages the visitor actually read.
//
// The first render is deliberately skipped so the landing page is not counted
// twice. Also installs the delegated tel:/mailto:/WhatsApp click tracking.
const RouteAnalytics = () => {
  const { pathname, search } = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    initContactLinkTracking();
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    trackPageView(pathname + search);
    trackMetaEvent('PageView');
  }, [pathname, search]);

  return null;
};

// Component to scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Component to conditionally render header/footer based on route
const ConditionalLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Skip link for accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <Header />
      <Breadcrumbs />
      <main id="main-content" className="pt-20" role="main">
        {children}
      </main>
      <Footer />
      <MobileStickyFooter />
      <CookieConsent />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <RouteAnalytics />
        <ScrollToTop />
        <ConditionalLayout>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div></div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/radiotherapy-anxiety" element={<Radiotherapy />} />
              <Route path="/persistent-pain" element={<PersistentPain />} />
              <Route path="/audios" element={<Audios />} />
              <Route path="/anxiety-stress" element={<AnxietyStress />} />
              <Route path="/medical-procedure-anxiety" element={<MedicalProcedureAnxiety />} />
              <Route path="/jaw-tension-hypnotherapy" element={<JawTensionHypnotherapy />} />
              <Route path="/bladder-urgency-hypnotherapy" element={<BladderUrgencyHypnotherapy />} />
              <Route path="/ibs-hypnotherapy" element={<IbsHypnotherapy />} />
              <Route path="/mri-anxiety-hypnotherapy" element={<MriAnxietyHypnotherapy />} />
              <Route path="/needle-phobia-hypnotherapy" element={<NeedlePhobiaHypnotherapy />} />
              <Route path="/stress-physical-symptoms" element={<StressPhysicalSymptoms />} />
              <Route path="/medical-anxiety-hypnotherapy" element={<MedicalAnxietyHypnotherapy />} />
              <Route path="/holiday-anxiety" element={<HolidayAnxiety />} />
              <Route path="/performance-anxiety" element={<PerformanceAnxiety />} />
              <Route path="/seasonal-anxiety" element={<SeasonalAnxiety />} />
              <Route path="/weight-mindset" element={<WeightMindset />} />
              <Route path="/burnout-recovery" element={<BurnoutRecovery />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:id" element={<BlogPostPage />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/quiz/stress-symptoms" element={<StressSymptomQuiz />} />
              <Route path="/quiz/medical-anxiety" element={<MedicalAnxietyQuiz />} />
              <Route path="/quiz/bladder-urgency" element={<BladderUrgencyQuiz />} />
              <Route path="/assessment" element={<Navigate to="/quiz/stress-symptoms" replace />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/book" element={<Book />} />
              <Route path="/thank-you" element={<ThankYou />} />
              {/* How Change Happens — composite case studies (single page) */}
              <Route path="/how-change-happens" element={<HowChangeHappens />} />
              <Route path="/how-change-happens/fear-of-driving" element={<Navigate to="/how-change-happens" replace />} />
              <Route path="/how-change-happens/bladder-urgency-at-night" element={<Navigate to="/how-change-happens" replace />} />
              <Route path="/how-change-happens/when-fear-interrupts-treatment" element={<Navigate to="/how-change-happens" replace />} />
              {/* Location Pages */}
              <Route path="/belfast-hypnotherapy" element={<BelfastHypnotherapy />} />
              <Route path="/garvagh-hypnotherapy" element={<Navigate to="/book" replace />} />
              <Route path="/derry-hypnotherapy" element={<DerryHypnotherapy />} />
              <Route path="/lisburn-hypnotherapy" element={<LisburnHypnotherapy />} />
              <Route path="/newry-hypnotherapy" element={<NewryHypnotherapy />} />
              <Route path="/bangor-hypnotherapy" element={<BangorHypnotherapy />} />
              <Route path="/newtownabbey-hypnotherapy" element={<NewtownabbeyHypnotherapy />} />
              <Route path="/carrickfergus-hypnotherapy" element={<CarrickfergusHypnotherapy />} />
              <Route path="/larne-hypnotherapy" element={<LarneHypnotherapy />} />
              <Route path="/antrim-hypnotherapy" element={<AntrimHypnotherapy />} />
              <Route path="/armagh-hypnotherapy" element={<ArmaghHypnotherapy />} />
              <Route path="/craigavon-hypnotherapy" element={<CraigavonHypnotherapy />} />
              <Route path="/enniskillen-hypnotherapy" element={<EnniskillenHypnotherapy />} />
              <Route path="/omagh-hypnotherapy" element={<OmaghHypnotherapy />} />
              <Route path="/strabane-hypnotherapy" element={<StrabaneHypnotherapy />} />
              <Route path="/downpatrick-hypnotherapy" element={<DownpatrickHypnotherapy />} />
              <Route path="/banbridge-hypnotherapy" element={<BanbridgeHypnotherapy />} />
              <Route path="/dungannon-hypnotherapy" element={<DungannonHypnotherapy />} />
              <Route path="/cookstown-hypnotherapy" element={<CookstownHypnotherapy />} />
              <Route path="/lurgan-hypnotherapy" element={<LurganHypnotherapy />} />
              <Route path="/portadown-hypnotherapy" element={<PortadownHypnotherapy />} />
              <Route path="/newtownards-hypnotherapy" element={<NewtownardsHypnotherapy />} />
              <Route path="/magherafelt-hypnotherapy" element={<MagherafeltHypnotherapy />} />
              <Route path="/maghera-hypnotherapy" element={<MagheraHypnotherapy />} />
              <Route path="/limavady-hypnotherapy" element={<LimavadyHypnotherapy />} />
              <Route path="/portrush-hypnotherapy" element={<PortrushHypnotherapy />} />
              <Route path="/portstewart-hypnotherapy" element={<PortstewartHypnotherapy />} />
              <Route path="/ballycastle-hypnotherapy" element={<BallycastleHypnotherapy />} />
              <Route path="/bushmills-hypnotherapy" element={<BushmillsHypnotherapy />} />
              {/* Service + Location Combo Pages */}
              <Route path="/lurgan-anxiety-stress" element={<LurganAnxietyStress />} />
              <Route path="/portadown-anxiety-stress" element={<PortadownAnxietyStress />} />
              <Route path="/newtownards-anxiety-stress" element={<NewtownardsAnxietyStress />} />
              <Route path="/magherafelt-anxiety-stress" element={<MagherafeltAnxietyStress />} />
              <Route path="/limavady-anxiety-stress" element={<LimavadyAnxietyStress />} />
              <Route path="/derry-anxiety-stress" element={<DerryAnxietyStress />} />
              <Route path="/lisburn-anxiety-stress" element={<LisburnAnxietyStress />} />
              <Route path="/newry-anxiety-stress" element={<NewryAnxietyStress />} />
              <Route path="/bangor-anxiety-stress" element={<BangorAnxietyStress />} />
              <Route path="/newtownabbey-anxiety-stress" element={<NewtownabbeyAnxietyStress />} />
              <Route path="/craigavon-anxiety-stress" element={<CraigavonAnxietyStress />} />
              <Route path="/carrickfergus-anxiety-stress" element={<CarrickfergusAnxietyStress />} />
              <Route path="/antrim-anxiety-stress" element={<AntrimAnxietyStress />} />
              <Route path="/omagh-anxiety-stress" element={<OmaghAnxietyStress />} />
              <Route path="/larne-anxiety-stress" element={<LarneAnxietyStress />} />
              <Route path="/derry-chronic-pain" element={<DerryChronicPain />} />
              <Route path="/lisburn-chronic-pain" element={<LisburnChronicPain />} />
              <Route path="/newry-chronic-pain" element={<NewryChronicPain />} />
              <Route path="/bangor-chronic-pain" element={<BangorChronicPain />} />
              <Route path="/newtownabbey-chronic-pain" element={<NewtownabbeyChronicPain />} />
              <Route path="/craigavon-chronic-pain" element={<CraigavonChronicPain />} />
              <Route path="/carrickfergus-chronic-pain" element={<CarrickfergusChronicPain />} />
              <Route path="/antrim-chronic-pain" element={<AntrimChronicPain />} />
              <Route path="/omagh-chronic-pain" element={<OmaghChronicPain />} />
              <Route path="/larne-chronic-pain" element={<LarneChronicPain />} />
              <Route path="/lurgan-chronic-pain" element={<LurganChronicPain />} />
              <Route path="/portadown-chronic-pain" element={<PortadownChronicPain />} />
              <Route path="/newtownards-chronic-pain" element={<NewtownardsChronicPain />} />
              <Route path="/magherafelt-chronic-pain" element={<MagherafeltChronicPain />} />
              <Route path="/limavady-chronic-pain" element={<LimavadyChronicPain />} />
              <Route path="/derry-weight-management" element={<DerryWeightManagement />} />
              <Route path="/lisburn-weight-management" element={<LisburnWeightManagement />} />
              <Route path="/newry-weight-management" element={<NewryWeightManagement />} />
              <Route path="/bangor-weight-management" element={<BangorWeightManagement />} />
              <Route path="/newtownabbey-weight-management" element={<NewtownabbeyWeightManagement />} />
              <Route path="/craigavon-weight-management" element={<CraigavonWeightManagement />} />
              <Route path="/carrickfergus-weight-management" element={<CarrickfergusWeightManagement />} />
              <Route path="/antrim-weight-management" element={<AntrimWeightManagement />} />
              <Route path="/omagh-weight-management" element={<OmaghWeightManagement />} />
              <Route path="/larne-weight-management" element={<LarneWeightManagement />} />
              <Route path="/lurgan-weight-management" element={<LurganWeightManagement />} />
              <Route path="/portadown-weight-management" element={<PortadownWeightManagement />} />
              <Route path="/newtownards-weight-management" element={<NewtownardsWeightManagement />} />
              <Route path="/magherafelt-weight-management" element={<MagherafeltWeightManagement />} />
              <Route path="/limavady-weight-management" element={<LimavadyWeightManagement />} />
              <Route path="/derry-medical-procedure-anxiety" element={<DerryMedicalProcedureAnxiety />} />
              <Route path="/lisburn-medical-procedure-anxiety" element={<LisburnMedicalProcedureAnxiety />} />
              <Route path="/newry-medical-procedure-anxiety" element={<NewryMedicalProcedureAnxiety />} />
              <Route path="/bangor-medical-procedure-anxiety" element={<BangorMedicalProcedureAnxiety />} />
              <Route path="/newtownabbey-medical-procedure-anxiety" element={<NewtownabbeyMedicalProcedureAnxiety />} />
              <Route path="/derry-quit-smoking" element={<DerryQuitSmoking />} />
              <Route path="/lisburn-quit-smoking" element={<LisburnQuitSmoking />} />
              <Route path="/newry-quit-smoking" element={<NewryQuitSmoking />} />
              <Route path="/bangor-quit-smoking" element={<BangorQuitSmoking />} />
              <Route path="/newtownabbey-quit-smoking" element={<NewtownabbeyQuitSmoking />} />
              <Route path="/craigavon-quit-smoking" element={<CraigavonQuitSmoking />} />
              <Route path="/carrickfergus-quit-smoking" element={<CarrickfergusQuitSmoking />} />
              <Route path="/antrim-quit-smoking" element={<AntrimQuitSmoking />} />
              <Route path="/omagh-quit-smoking" element={<OmaghQuitSmoking />} />
              <Route path="/larne-quit-smoking" element={<LarneQuitSmoking />} />
              <Route path="/craigavon-medical-anxiety" element={<CraigavonMedicalAnxiety />} />
              <Route path="/carrickfergus-medical-anxiety" element={<CarrickfergusMedicalAnxiety />} />
              <Route path="/antrim-medical-anxiety" element={<AntrimMedicalAnxiety />} />
              <Route path="/omagh-medical-anxiety" element={<OmaghMedicalAnxiety />} />
              <Route path="/larne-medical-anxiety" element={<LarneMedicalAnxiety />} />
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/enquiries"
                element={
                  <ProtectedRoute>
                    <AdminEnquiries />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/polls"
                element={
                  <ProtectedRoute>
                    <AdminPolls />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/audio-downloads"
                element={
                  <ProtectedRoute>
                    <AdminAudioDownloads />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/subscriptions"
                element={
                  <ProtectedRoute>
                    <AdminSubscriptions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/blog-posts"
                element={
                  <ProtectedRoute>
                    <AdminBlogPosts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/blog-audit"
                element={
                  <ProtectedRoute>
                    <AdminBlogAudit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/seo-performance"
                element={
                  <ProtectedRoute>
                    <SEOPerformance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/quiz-responses"
                element={
                  <ProtectedRoute>
                    <AdminQuizResponses />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/bookings"
                element={
                  <ProtectedRoute>
                    <AdminBookings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedRoute>
                    <AdminAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/review-requests"
                element={
                  <ProtectedRoute>
                    <AdminReviewRequests />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </React.Suspense>
        </ConditionalLayout>
      </Router>
    </HelmetProvider>
  );
}

export default App;
